import React, { ReactElement, FunctionComponent, memo, useCallback, useEffect, useRef } from 'react';
import { useCheckBrowserShareAndDowloadAbility } from '../../utils';
import { ISceneActions, useDataStore, useSceneStore } from '../../zustand';
import { IRoot, ISceneComp, ITriggerTypes } from '@r3f-component-data-structure';
import { ImageTrackerWrapper } from './ImageTrackerWrapper/ImageTrackerWrapper';
import SnapshotUI from '../SnapshotUI/SnapshotUI';
import FaceTrackerWrapper from './FaceTrackerWrapper/FaceTrackerWrapper';
import { isAbstractComponent } from '@r3f/utils/general';
import { useThree } from '@react-three/fiber';
import { useAddEntityActionBehaviour, useGetSubscribedSceneTransitionCategoryRef, useGetSubscribedSceneTransitionPhaseRef } from '../hoc/hocCustomHooks';
import WorldXR from './WorldXRTracker/WebXR';
import { Jute } from '../..';
import Content360Wrapper from './Content360Wrapper/Content360Wrapper';
import { IImageSceneDict } from '../Root';
import { useProjectHas360Scenes } from './Content360Wrapper/content360Hooks';
import { useProjectHasImageTrackedScenes, useProjectHasWorldTrackedScenes, useProjectHasFaceTrackedScenes } from './ImageTrackerWrapper/hooks';
import WorldInstantTrackerWrapper from './WorldInstantTrackerWrapper/WorldInstantTrackerWrapper';
import WorldTrackerWrapper from './WorldTrackerWrapper/WorldTrackerWrapper';

type IParentProps = {
	children: {
		imgTracked: IImageSceneDict;
		wldTracked: ReactElement[];
		faceTracked: ReactElement[];
		content360: ReactElement[];
	} | null;
	onShowContentFirstTime?: () => any;
};

export const TrackerGroups: FunctionComponent<IParentProps> = ({ children, onShowContentFirstTime }) => {
	const { wldTracked: worldTrackedScenes, imgTracked: imageTrackedScenes, faceTracked: faceTrackedScenes, content360: content360Scenes } = children || {};
	const { scene: threeScene } = useThree();
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	const webxr = useDataStore(({ webXR }) => webXR);
	const rootId = useDataStore(({ rootId }) => rootId);
	const firstSceneId = (componentsById?.[rootId!] as IRoot)?.children[0];
	const activeSceneId = useSceneStore(({ activeSceneId }) => activeSceneId);
	const previousSceneId = useSceneStore(({ previousSceneId }) => previousSceneId);
	const transitionPhaseDict = useSceneStore(({ transitionPhase }) => transitionPhase);
	const sceneTransCatRef = useGetSubscribedSceneTransitionCategoryRef(activeSceneId ?? firstSceneId);
	const sceneTransPhaseRef = useGetSubscribedSceneTransitionPhaseRef(activeSceneId ?? firstSceneId);

	const scene = componentsById?.[activeSceneId ?? firstSceneId] as ISceneComp;

	const projectHas360Scenes = useProjectHas360Scenes()
	const projectHasImageTrackedScenes = useProjectHasImageTrackedScenes();
	const projectHasWorldTrackedScenes = useProjectHasWorldTrackedScenes();
	const projectHasFaceTrackedScenes = useProjectHasFaceTrackedScenes();

	const entityMouthOpenActionHandler = useAddEntityActionBehaviour(activeSceneId ?? firstSceneId, scene,  sceneTransCatRef, sceneTransPhaseRef, ITriggerTypes.onMouthOpen)
	const entityHeadTiltLeftActionHandler = useAddEntityActionBehaviour(activeSceneId ?? firstSceneId, scene, sceneTransCatRef, sceneTransPhaseRef, ITriggerTypes.onHeadTiltLeft)
	const entityHeadTiltRightActionHandler = useAddEntityActionBehaviour(activeSceneId ?? firstSceneId, scene, sceneTransCatRef, sceneTransPhaseRef, ITriggerTypes.onHeadTiltRight)
	const entityHeadShakeActionHandler = useAddEntityActionBehaviour(activeSceneId ?? firstSceneId, scene, sceneTransCatRef, sceneTransPhaseRef, ITriggerTypes.onHeadShake)

	// In the scene store rather than local state: the preview's orbit camera needs the same answer, and
	// re-deriving the exitFinish rule in a second place is how the two would drift apart.
	const sceneDispatch = useSceneStore(({ dispatch }) => dispatch);
	const enabledTrackingType = useSceneStore(({ enabledTrackingType }) => enabledTrackingType);

	let browserInfo = useCheckBrowserShareAndDowloadAbility();

	const trackingHasBeenFirstTimeReady = useRef(false);

	const _onShowContentFirstTime = useCallback(() => {
		if (trackingHasBeenFirstTimeReady.current) return;
		trackingHasBeenFirstTimeReady.current = true;
		onShowContentFirstTime?.();
	}, [onShowContentFirstTime]);

	useEffect(() => {
		if (!componentsById || !rootId) return;

		// Hide any entities that were hidden in Designer
		Object.entries(componentsById).forEach(([id, component]) => {
			if (isAbstractComponent(component)) return;
			const prospectiveGroupId = `toggleVisibilityGroup-${id}`;
			const threeGroup = threeScene.getObjectByName(prospectiveGroupId);
			if (threeGroup) {
				threeGroup.visible = !component.isHidden;
			}
		});

		const firstSceneId = (componentsById[rootId] as IRoot).children[0];
		const activeScene = componentsById[activeSceneId || firstSceneId] as ISceneComp;
		const prevSceneTransitionPhase = transitionPhaseDict[previousSceneId || firstSceneId];
		// enable first scene tracking before link to scene action fired
		// enable/disable new tracking type once previous scene has fully exited
		if (activeSceneId === firstSceneId || prevSceneTransitionPhase === 'exitFinish') {
			sceneDispatch?.({
				type: ISceneActions.SET_ENABLED_TRACKING_TYPE,
				enabledTrackingType: activeScene.trackingType ?? null
			});
		}
	}, [transitionPhaseDict, componentsById, previousSceneId, activeSceneId, rootId, sceneDispatch]);

	const onMouthOpenChange = (open: boolean) => {
		if (open) {
			entityMouthOpenActionHandler(scene.actions!.onMouthOpen!)
		}
	}

	const onHeadTilt = (side: 'left' | 'right') => {
		if (side === 'left') {
			entityHeadTiltLeftActionHandler(scene.actions!.onHeadTiltLeft!)
			return;
		}
		entityHeadTiltRightActionHandler(scene.actions!.onHeadTiltRight!)
	}

	const onHeadShake = () => {
		entityHeadShakeActionHandler(scene.actions!.onHeadShake!)
	}
	
	return (
		<>
			{/* if ios 12 hide snapshot ui due to file saving and sharing issues */}
			{!browserInfo.iOS12HideSave && <SnapshotUI />}

			{(webxr === null || !Jute.isHeadset) ? 
				<>
					{projectHas360Scenes && <Content360Wrapper enabled={enabledTrackingType === 'content360'} onShowContentFirstTime={_onShowContentFirstTime} >
						{content360Scenes}
					</Content360Wrapper>}
					{projectHasWorldTrackedScenes && <>
						{Jute.isAppClip || Jute.isMobileWebXRExperience ?
							<WorldTrackerWrapper enabled={enabledTrackingType === 'world'} onShowContentFirstTime={_onShowContentFirstTime}>
								{worldTrackedScenes}
							</WorldTrackerWrapper> :
							<WorldInstantTrackerWrapper enabled={enabledTrackingType === 'world'} onShowContentFirstTime={_onShowContentFirstTime}>
								{worldTrackedScenes}
							</WorldInstantTrackerWrapper> 
						}</>
					}
					{projectHasImageTrackedScenes && <ImageTrackerWrapper enabled={enabledTrackingType === 'image'}>
						{imageTrackedScenes}
					</ImageTrackerWrapper>}
					{projectHasFaceTrackedScenes && <FaceTrackerWrapper 
						enabled={enabledTrackingType === 'face'} 
						onMouthOpenChange={!!scene?.actions?.onMouthOpen ? onMouthOpenChange : undefined}
						onHeadTilt={!!(scene?.actions?.onHeadTiltLeft ?? scene?.actions?.onHeadTiltRight) ? onHeadTilt : undefined}
						onHeadShake={!!scene?.actions?.onHeadShake ? onHeadShake : undefined}
						onShowContentFirstTime={_onShowContentFirstTime}
					>
						{faceTrackedScenes}
					</FaceTrackerWrapper>}
				</> : 
				<WorldXR enabled={enabledTrackingType === 'world'} onShowContentFirstTime={_onShowContentFirstTime} >
					{[...worldTrackedScenes ?? [], ...content360Scenes ?? []]}
				</WorldXR> 
			}
		</>
	);
};

TrackerGroups.displayName = 'TrackerGroups';
export default memo(TrackerGroups);
